import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import {
    Alert,
    Badge,
    Button,
    Card,
    CardBody,
    Progress,
} from 'reactstrap';
import * as FileSaver from 'file-saver';
import * as XLSX from '@e965/xlsx';
import moment from 'moment';
import Loader from '../Loader/Loader';
import SyncFileImportSection from '../SynchronisationPanel/SyncFileImportSection/SyncFileImportSection';
import Utils from '../../services/Utils';
import {
    FILE_EXTENSIONS,
    buildCreatePayloads,
    buildSampleWorkbook,
    buildUpdatePayload,
    prepareArticlesImport,
    readArticlesFile,
} from '../../services/articlesImport';
import { canSyncArticleStocks } from '../../services/syncImportAccess';

import './ArticlesImporter.css';

const actionBadgeColor = action => {
    switch (action) {
        case 'create':
            return 'success';
        case 'update':
            return 'info';
        case 'skip':
            return 'secondary';
        case 'error':
            return 'danger';
        default:
            return 'light';
    }
};

/**
 * Article catalogue import UI (Synchronisation panel → Articles → Catalogue).
 *
 * @param {{ visible: boolean }} props
 */
const ArticlesImporter = ({ visible }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();

    const { salepoints } = useSelector(state => state.salepoints);
    const { articlecategories } = useSelector(state => state.articlecategories);
    const { setting } = useSelector(state => state.settings);
    const { user } = useSelector(state => state.auth);

    const [fileName, setFileName] = useState('');
    const [groups, setGroups] = useState([]);
    const [validation, setValidation] = useState(null);
    const [existingArticles, setExistingArticles] = useState([]);
    const [isLoadingArticles, setIsLoadingArticles] = useState(false);
    const [pendingFile, setPendingFile] = useState(null);
    const [isImporting, setIsImporting] = useState(false);
    const [importProgress, setImportProgress] = useState({ done: 0, total: 0 });
    const [importResults, setImportResults] = useState(null);
    const [uploadError, setUploadError] = useState(null);

    const canUpdate = canSyncArticleStocks(user);

    useEffect(() => {
        if (!visible)
            return undefined;

        let cancelled = false;

        const bootstrap = async () => {
            const [
                { loadSalepoints },
                { loadArticlecategories },
            ] = await Promise.all([
                import('../../redux/modules/salepoints'),
                import('../../redux/modules/articlecategories'),
            ]);

            if (cancelled)
                return;

            dispatch(loadSalepoints());
            dispatch(loadArticlecategories());
        };

        bootstrap();
        return () => { cancelled = true; };
    }, [dispatch, visible]);

    useEffect(() => {
        if (!visible)
            return undefined;

        let cancelled = false;

        const loadExisting = async () => {
            setIsLoadingArticles(true);
            try {
                const ArticlesApi = (await import('../../services/rest/Articles')).default;
                const data = await ArticlesApi.list({
                    types: 'product',
                    fields: [
                        '_id',
                        'name',
                        'short_description',
                        'category',
                        'stock_type',
                        'pos',
                        'supplier',
                        'sort_weight',
                        'variants',
                    ],
                });
                if (!cancelled)
                    setExistingArticles(data.articles || []);
            } catch (_err) {
                if (!cancelled)
                    setExistingArticles([]);
            } finally {
                if (!cancelled)
                    setIsLoadingArticles(false);
            }
        };

        loadExisting();
        return () => { cancelled = true; };
    }, [visible]);

    const categoryIds = useMemo(
        () => (articlecategories || []).map(category => category._id),
        [articlecategories]
    );

    const downloadSample = () => {
        const workbook = buildSampleWorkbook({
            lang: i18n.language,
            categoryId: categoryIds[0] || '',
            salepointIds: (salepoints || []).map(s => s._id),
        });
        const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        FileSaver.saveAs(
            new Blob([buffer], { type: 'application/octet-stream' }),
            'articles_import.xlsx'
        );
    };

    const resetPreview = () => {
        setGroups([]);
        setValidation(null);
        setImportResults(null);
        setUploadError(null);
        setFileName('');
        setPendingFile(null);
        setImportProgress({ done: 0, total: 0 });
    };

    const processFile = useCallback(async (file, articles) => {
        const extension = file.name.split('.').pop()?.toLowerCase();
        if (!FILE_EXTENSIONS.includes(extension)) {
            setUploadError(t('articles.import.unsupported_file_type'));
            setFileName('');
            setGroups([]);
            setValidation(null);
            setPendingFile(null);
            return;
        }

        try {
            const rows = await readArticlesFile(file);
            const prepared = prepareArticlesImport({
                rows,
                articles,
                categoryIds,
            });

            if (!canUpdate && prepared.groups.some(g => g.action === 'update')) {
                prepared.validation.errors.push({
                    key: 'articles.import.update_requires_edit',
                });
                prepared.validation.valid = false;
            }

            setFileName(file.name);
            setGroups(prepared.groups);
            setValidation(prepared.validation);
            setPendingFile(null);
        } catch (err) {
            const messageKey = err?.message?.startsWith('articles.')
                ? err.message
                : 'articles.import.invalid_file_content';
            setUploadError(t(messageKey));
            setFileName('');
            setGroups([]);
            setValidation(null);
            setPendingFile(null);
        }
    }, [categoryIds, canUpdate, t]);

    const onFileSelected = useCallback(async file => {
        setUploadError(null);
        setImportResults(null);

        if (!file)
            return;

        if (isLoadingArticles) {
            setPendingFile(file);
            setFileName(file.name);
            setGroups([]);
            setValidation(null);
            return;
        }

        await processFile(file, existingArticles);
    }, [isLoadingArticles, existingArticles, processFile]);

    useEffect(() => {
        if (!pendingFile || isLoadingArticles)
            return undefined;

        const file = pendingFile;
        setPendingFile(null);
        processFile(file, existingArticles);
        return undefined;
    }, [pendingFile, isLoadingArticles, existingArticles, processFile]);

    const runImport = async () => {
        if (!validation?.valid || !groups.length || isImporting)
            return;

        const ArticlesApi = (await import('../../services/rest/Articles')).default;
        const importedAt = moment().format('YYYYMMDDHHmmss');
        const createGroups = groups.filter(g => g.action === 'create');
        const updateGroups = groups.filter(g => g.action === 'update');
        const createPayloads = buildCreatePayloads(createGroups, {
            currency: Utils.currency(),
            langs: Utils.langs(),
            vat: setting?.customer?.tax?.vat_articles || 0,
            importedAt,
        });

        const total = createPayloads.length + updateGroups.length;
        setIsImporting(true);
        setImportProgress({ done: 0, total });
        setImportResults(null);

        const created = [];
        const updated = [];
        const failed = [];
        let done = 0;

        for (let index = 0; index < createPayloads.length; index += 1) {
            const payload = createPayloads[index];
            try {
                const data = await ArticlesApi.create(payload);
                created.push({
                    name: payload.name?.[Utils.default_lang()] || payload.name?.fr || createGroups[index]?.name,
                    articleId: data.article?._id,
                    variants: payload.variants.length,
                });
            } catch (err) {
                failed.push({
                    name: payload.name?.[Utils.default_lang()] || payload.name?.fr || createGroups[index]?.name,
                    message: err?.message || String(err),
                });
            }
            done += 1;
            setImportProgress({ done, total });
        }

        for (let index = 0; index < updateGroups.length; index += 1) {
            const group = updateGroups[index];
            try {
                const payload = buildUpdatePayload(group, {
                    currency: Utils.currency(),
                    langs: Utils.langs(),
                    vat: setting?.customer?.tax?.vat_articles || 0,
                });
                const data = await ArticlesApi.update(payload);
                updated.push({
                    name: Utils.localized_or_fallback(data.article?.name || payload.name, i18n.language)
                        || payload._id,
                    articleId: payload._id,
                    variants: group.rows.length,
                });
            } catch (err) {
                failed.push({
                    name: Utils.localized_or_fallback(group.article?.name, i18n.language) || group.article?._id,
                    message: err?.message || String(err),
                });
            }
            done += 1;
            setImportProgress({ done, total });
        }

        setImportResults({ created, updated, failed, importedAt });
        setIsImporting(false);

        if (created.length || updated.length) {
            try {
                const data = await ArticlesApi.list({
                    types: 'product',
                    fields: ['_id', 'name', 'category', 'stock_type', 'pos', 'variants'],
                });
                setExistingArticles(data.articles || []);
            } catch (_err) {
                // ignore refresh errors
            }
        }
    };

    if (!visible)
        return null;

    if (!salepoints || !articlecategories || !setting)
        return <Loader />;

    const canImport = !!validation?.valid && groups.length > 0 && !isImporting;
    const isPreparingFile = !!pendingFile && isLoadingArticles;
    const previewRows = validation?.previewRows || [];
    const createCount = previewRows.filter(r => r.action === 'create').length;
    const updateCount = previewRows.filter(r => r.action === 'update').length;
    const errorCount = previewRows.filter(r => r.action === 'error').length;

    const uploadHelp = (
        <p>
            <Trans
                i18nKey="articles.import.upload_help"
                components={{
                    code: <code className="SyncFileImportSection-code" />,
                }}
            />
        </p>
    );

    return (
        <div className="ArticlesImporter">
            <SyncFileImportSection
                title={t('articles.import.upload_title')}
                help={uploadHelp}
                formats={t('common.supported_spreadsheet_formats')}
                accept=".csv,.xls,.xlsx,.ods,.numbers"
                disabled={isImporting}
                fileName={fileName}
                onFile={onFileSelected}
                downloadSlot={(
                    <Button color="info" size="sm" onClick={downloadSample} disabled={isImporting}>
                        <i className="fa fa-download" />
                        &nbsp;{t('articles.import.download_sample')}
                    </Button>
                )}
            >
                {(validation || importResults || uploadError) && (
                    <Button color="secondary" size="sm" className="mt-3" onClick={resetPreview} disabled={isImporting}>
                        {t('articles.import.reset')}
                    </Button>
                )}
                {uploadError && <Alert color="danger" className="mt-3 mb-0">{uploadError}</Alert>}
                {isPreparingFile && (
                    <Alert color="info" className="mt-3 mb-0">
                        {t('articles.import.loading_existing')}
                    </Alert>
                )}
            </SyncFileImportSection>

            {isImporting && (
                <div className="mb-3">
                    <p className="text-muted">
                        {t('articles.import.importing', {
                            done: importProgress.done,
                            total: importProgress.total,
                        })}
                    </p>
                    <Progress
                        value={importProgress.total
                            ? (100 * importProgress.done) / importProgress.total
                            : 0}
                    />
                </div>
            )}

            {validation && !importResults && (
                <Card className="mb-3">
                    <CardBody>
                        <h5>{t('articles.import.preview_title')}</h5>
                        <p>
                            {t('articles.import.preview_summary', {
                                create: createCount,
                                update: updateCount,
                                error: errorCount,
                            })}
                        </p>

                        {validation.errors?.length > 0 && (
                            <Card className="mb-3 ArticlesImporter-messagesCard ArticlesImporter-messagesCard--danger">
                                <CardBody>
                                    <h6>{t('articles.import.errors_title')}</h6>
                                    <div className="ArticlesImporter-messagesList">
                                        <ul>
                                            {validation.errors.map((err, index) => (
                                                <li key={`err-${index}`}>{t(err.key, err.params)}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </CardBody>
                            </Card>
                        )}

                        {validation.warnings?.length > 0 && (
                            <Card className="mb-3 ArticlesImporter-messagesCard ArticlesImporter-messagesCard--warning">
                                <CardBody>
                                    <h6>{t('articles.import.warnings_title')}</h6>
                                    <div className="ArticlesImporter-messagesList">
                                        <ul>
                                            {validation.warnings.map((warn, index) => (
                                                <li key={`warn-${index}`}>{t(warn.key, warn.params)}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </CardBody>
                            </Card>
                        )}

                        {validation.valid && (
                            <Alert color="success" className="mb-3">
                                {t('articles.import.ready')}
                            </Alert>
                        )}

                        {previewRows.length > 0 && (
                            <div className="table-responsive">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>{t('articles.import.col_action')}</th>
                                            <th>{t('articles.import.col_article')}</th>
                                            <th>{t('articles.import.col_variant')}</th>
                                            <th>{t('articles.import.col_details')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewRows.map((row, index) => (
                                            <tr key={`preview-${index}`}>
                                                <td>
                                                    <Badge color={actionBadgeColor(row.action)}>
                                                        {t(`articles.import.action_${row.action}`)}
                                                    </Badge>
                                                </td>
                                                <td>{row.name}</td>
                                                <td>{row.variantName || (row.variants ? `${row.variants}` : '')}</td>
                                                <td>
                                                    {row.line
                                                        ? t('articles.import.line_n', { line: row.line })
                                                        : (row.lines || []).join(', ')}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <Button color="primary" disabled={!canImport} onClick={runImport}>
                            {t('articles.import.do_import')}
                        </Button>
                    </CardBody>
                </Card>
            )}

            {importResults && (
                <Card className="mb-3">
                    <CardBody>
                        <h5>{t('articles.import.result_title')}</h5>
                        <p>
                            {t('articles.import.result_summary', {
                                created: importResults.created.length,
                                updated: importResults.updated.length,
                                failed: importResults.failed.length,
                                importedAt: importResults.importedAt,
                            })}
                        </p>
                        {importResults.failed.length > 0 && (
                            <Alert color="danger">
                                <ul className="mb-0">
                                    {importResults.failed.map((item, index) => (
                                        <li key={`fail-${index}`}>{item.name}: {item.message}</li>
                                    ))}
                                </ul>
                            </Alert>
                        )}
                        <Button color="secondary" tag={Link} to="/articles" className="mr-2">
                            {t('articles.import.back_to_list')}
                        </Button>
                        <Button color="secondary" onClick={resetPreview}>
                            {t('articles.import.reset')}
                        </Button>
                    </CardBody>
                </Card>
            )}
        </div>
    );
};

export default ArticlesImporter;
